<!--
  One group of ELSA's claims — risks, signals or actions (AB#21202).

  The evidence line is not decoration and is not optional. It is the reason
  this card can be shown at all: a reader can check any claim against the
  timeline event it names, which is what separates it from a model's opinion.
  The backend drops claims that arrive without evidence, so this component
  never has to decide what to do about one.

  An empty group renders nothing rather than "None" — on an order that is going
  fine, three empty headings is noise, and the absence of risks is already said
  by the status pill.
-->
<template>
  <div v-if="claims.length" class="elsa-group" :data-test="`elsa-${kind}s`">
    <h4 class="elsa-group-title">{{ title }}</h4>
    <div v-for="(entry, index) in claims" :key="index" class="elsa-claim" :class="`elsa-claim--${kind}`">
      <span class="elsa-claim-mark" aria-hidden="true">{{ MARKS[kind] }}</span>
      <span class="elsa-claim-text">{{ entry.claim }}</span>
      <span class="elsa-claim-evidence">{{ entry.evidence }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Claim } from '@/composables/useOrderAnalysis';

type ClaimKind = 'risk' | 'good' | 'action';

defineProps<{ title: string; kind: ClaimKind; claims: Claim[] }>();

const MARKS: Record<ClaimKind, string> = {
  risk: '▲',
  good: '●',
  action: '→',
};
</script>

<style scoped>
.elsa-group {
  margin-top: 14px;
}

.elsa-group-title {
  font-size: 10.5px;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #7d8a87;
  margin: 0 0 7px;
  font-weight: 500;
}

.elsa-claim {
  display: grid;
  grid-template-columns: 16px 1fr;
  gap: 3px 9px;
  padding: 8px 0;
  border-top: 1px solid #e5e7eb;
}

.elsa-claim:first-of-type {
  border-top: none;
  padding-top: 2px;
}

.elsa-claim-mark {
  font-size: 13px;
  line-height: 1.5;
}

.elsa-claim--risk .elsa-claim-mark {
  color: #c4362c;
}
.elsa-claim--good .elsa-claim-mark {
  color: #1e8e5a;
}
.elsa-claim--action .elsa-claim-mark {
  color: #1b8670;
}

.elsa-claim-text {
  font-size: 13.5px;
  color: #16211e;
}

.elsa-claim-evidence {
  grid-column: 2;
  font-size: 12px;
  color: #7d8a87;
  line-height: 1.5;
}
</style>
